// src/hooks/usePasswordCriteria.js
import { useEffect, useState } from "react";
import { validatePassword } from "firebase/auth";
import { POLICY_REGEX } from "../utils/constants";

/**
 * @typedef {object} PasswordCriteria
 * @property {boolean} length
 *   `true` when the password satisfies the configured minimum length.
 * @property {boolean} upper
 *   `true` when the password contains an uppercase letter.
 * @property {boolean} lower
 *   `true` when the password contains a lowercase letter.
 * @property {boolean} number
 *   `true` when the password contains a numeric character.
 * @property {boolean} special
 *   `true` when the password contains a non-alphanumeric character.
 */

/**
 * Returns an object { length, upper, lower, number, special } that
 * updates whenever the password value changes.
 *
 * @param {string} password
 * @param {object} auth
 *   Firebase Auth instance used by `validatePassword`. The hook falls back to
 *   regex checks when the SDK helper fails or no password policy is available.
 * @returns {PasswordCriteria}
 */
export default function usePasswordCriteria(password, auth) {
  const [criteria, setCriteria] = useState({
    length: false,
    upper: false,
    lower: false,
    number: false,
    special: false,
  });

  useEffect(() => {
    let cancelled = false;

    const compute = async () => {
      /* Try the Identity-Platform helper (if policy is enabled) */
      try {
        const s = await validatePassword(auth, password);
        if (cancelled) return;
        setCriteria({
          length: s.meetsMinPasswordLength === true,
          upper: s.containsUppercaseLetter === true,
          lower: s.containsLowercaseLetter === true,
          number: s.containsNumericCharacter === true,
          special: s.containsNonAlphanumericCharacter === true,
        });
        return;
      } catch (_) {
        /* ignore → fallback */
      }

      /* Regex fallback so it also works offline */
      setCriteria({
        length: POLICY_REGEX.length.test(password),
        upper: POLICY_REGEX.upper.test(password),
        lower: POLICY_REGEX.lower.test(password),
        number: POLICY_REGEX.number.test(password),
        special: POLICY_REGEX.special.test(password),
      });
    };

    compute();
    return () => {
      cancelled = true;
    };
  }, [password, auth]);

  return criteria;
}
